import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@api';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import './GameAuth.css';

function GameAuth() {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editModeScores, setEditModeScores] = useState(false);
  const [periodScores, setPeriodScores] = useState([]);
  const [statValues, setStatValues] = useState({});
  const [stats, setStats] = useState([]);  // Add stats state
  const [editFinalScores, setEditFinalScores] = useState(false); // New state 
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    video_url: '',
    location: '',
    date: '',
    time: '',
    // status: ''
  });
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [finalScoreForm, setFinalScoreForm] = useState({
    score_home: 0,
    score_away: 0,
  });
  const [isSaving, setIsSaving] = useState(false)
  const [originalStatValues, setOriginalStatValues] = useState({});
  const [originalFinalScoreForm, setOriginalFinalScoreForm] = useState({ score_home: 0, score_away: 0 });




  const { id } = useParams();
  const fetchGame = async () => {
    try {
      const res = await api.get(`/api/gamesv2/${id}`);

      const { game, homeRoster, awayRoster, periodScores, allStats, playerStats } = res.data;
      console.log(res.data)
      const sortedStats = (allStats || [])
        .filter(stat => !stat.hidden)
        .sort((a, b) => a.order - b.order);

      // 🧠 Normalize homeTeam and awayTeam with players
      const homePlayers = homeRoster?.map((r) => r.player) || [];
      const awayPlayers = awayRoster?.map((r) => r.player) || [];

      const transformedGame = {
        ...game,
        homeTeam: { ...game.home_team?.team, players: homePlayers },
        awayTeam: { ...game.away_team?.team, players: awayPlayers },
      };

      setGame(transformedGame);
      setFinalScoreForm({
        score_home: game.score_home ?? 0,
        score_away: game.score_away ?? 0,
      });

      setStats(sortedStats);

      // 🧠 Group period scores into one per period with both team scores
      const groupedPeriodScores = [];
      for (const score of periodScores) {
        const existing = groupedPeriodScores.find(p => p.gamePeriod?.id === score.gamePeriod?.id);
        if (!existing) {
          groupedPeriodScores.push({
            id: score.id, // pick one score ID (any)
            gamePeriod: score.gamePeriod,
            period_score_team1: score.team_id === game.home_team_id ? score.score : null,
            period_score_team2: score.team_id === game.away_team_id ? score.score : null,
          });
        } else {
          if (score.team_id === game.home_team_id) existing.period_score_team1 = score.score;
          if (score.team_id === game.away_team_id) existing.period_score_team2 = score.score;
        }
      }

      setPeriodScores(groupedPeriodScores);

      // 🧠 Build player stat values
      const values = {};
      playerStats?.forEach((stat) => {
        values[`${stat.player_id}-${stat.stat_id}`] = stat.value;
      });
      setStatValues(values);
      setStatValues(values);
      setOriginalStatValues(values); // ✅ save original stats

      setFinalScoreForm({
        score_home: game.score_home ?? 0,
        score_away: game.score_away ?? 0,
      });
      setOriginalFinalScoreForm({
        score_home: game.score_home ?? 0,
        score_away: game.score_away ?? 0,
      });
      const ptsStatId = sortedStats.find(stat => stat.abbreviation === "PTS")?.id;

      const totalPointsHome = homePlayers.reduce((sum, player) => {
        return sum + (values[`${player.id}-${ptsStatId}`] || 0);
      }, 0);

      const totalPointsAway = awayPlayers.reduce((sum, player) => {
        return sum + (values[`${player.id}-${ptsStatId}`] || 0);
      }, 0);

      const mismatchDetected =
        totalPointsHome !== (game.score_home ?? 0) ||
        totalPointsAway !== (game.score_away ?? 0);

      sessionStorage.setItem(`mismatch-${game.id}`, JSON.stringify(mismatchDetected));

    } catch (err) {
      console.error("Error fetching game:", err);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchGame();

  }, [id]);

  // Initialize stat values from existing player stats
  const initializeStatValues = (data) => {
    if (!data || !data.playerStats) return;
    const values = {};
    data.playerStats.forEach((stat) => {
      values[`${stat.player_id}-${stat.stat_id}`] = stat.value;
    });
    setStatValues(values);
  };

  // Handle stat input change
  const handleInputChange = (player_id, stat_id, value) => {
    setStatValues((prev) => ({
      ...prev,
      [`${player_id}-${stat_id}`]: value,
    }));
  };

  // Submit updated stats
  const handleSubmit = async () => {
    if (!game || !game.id) {
      alert("Game ID is missing. Cannot update stats.");
      return;
    }
    // Get the actual stat_id for PTS (points)
    const ptsStatId = stats.find(stat => stat.abbreviation === "PTS")?.id;

    const totalPointsHome = game.homeTeam?.players.reduce((sum, player) => {
      return sum + (statValues[`${player.id}-${ptsStatId}`] || 0);
    }, 0);
    const totalPointsAway = game.awayTeam?.players.reduce((sum, player) => {
      return sum + (statValues[`${player.id}-${ptsStatId}`] || 0);
    }, 0);


    const statsToUpdate = [];
    Object.keys(statValues).forEach((key) => {
      const [player_id, stat_id] = key.split('-').map(Number);
      statsToUpdate.push({
        player_id,
        game_id: game.id,
        stat_id,
        value: statValues[key] || 0,
      });
    });
    setIsSaving(true);
    try {
      await api.put('/api/player-game-stats', {
        player_id: statsToUpdate[0]?.player_id,
        game_id: game.id,
        stats: statsToUpdate,
      });

      setEditMode(false);
    } catch (error) {
      console.error('❌ Error updating stats:', error.response?.data || error);
      alert('Failed to update stats.');
    } finally {
      setIsSaving(false)
    }
  };


  // const handleSaveScores = async () => {
  //   try {
  //     const finalPeriod = periodScores.find(p => p.gamePeriod?.name === "Final");
  //     const finalScoreTeam1 = finalPeriod?.period_score_team1 ?? 0;
  //     const finalScoreTeam2 = finalPeriod?.period_score_team2 ?? 0;

  //     // 1. Save period scores
  //     await api.put(`/api/gamesv2/${game.id}/period-scores`, {
  //       game_id: game.id,
  //       scores: periodScores.map(period => ({
  //         id: period.id,
  //         period_score_team1: period.period_score_team1,
  //         period_score_team2: period.period_score_team2,
  //       })),
  //     });

  //     // 2. Also update final score in GameV2
  //     await api.put(`/api/gamesv2/${game.id}/scores`, {
  //       score_home: finalScoreTeam1,
  //       score_away: finalScoreTeam2,
  //     });

  //     alert("Scores updated successfully!");
  //     setEditModeScores(false);
  //     setEditFinalScores(false);
  //     fetchGame();
  //   } catch (error) {
  //     console.error("Error updating scores:", error);
  //     alert("Failed to update scores.");
  //   }
  // };

  // ✅ move this outside of handleSubmit
  const handleSaveFinalScore = async () => {

    try {
      await api.put(`/api/gamesv2/${game.id}/scores`, {
        score_home: finalScoreForm.score_home,
        score_away: finalScoreForm.score_away,
      });
      alert("Final game score updated!");
      setEditFinalScores(false);
      fetchGame(); // refresh data
    } catch (err) {
      console.error("Error updating final score:", err);
      alert("Failed to update final score.");
    }
  };


  const handleUpdateGameDetails = async (e) => {
    e.preventDefault();

    // Convert video URL to embed format
    let videoUrl = formData.video_url;
    if (videoUrl.includes('youtube.com')) {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0]; // Extract video ID
      videoUrl = `https://www.youtube.com/embed/${videoId}`; // Convert to embed URL
    }

    try {
      const { location, time, status, date } = formData;
      const isoDateTime = new Date(`${date}T${time}:00`).toISOString();

      await api.put(`/api/gamesv2/${game.id}/details`, {
        video_url: videoUrl,
        location,
        date,      // 👈 send raw date
        time,      // 👈 send raw time
        status,
      });



      alert("Game details updated successfully!");
      setShowModal(false); // Close modal after updating
      fetchGame(); // Refresh game data after updating
    } catch (error) {
      console.error("Error updating game details:", error);
      alert("Failed to update game details.");
    }
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "TBD";
    const date = new Date(dateTime);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  };





  const handleScoreChange = (periodId, team, value) => {
    setPeriodScores((prevScores) =>
      prevScores.map((period) =>
        period.id === periodId
          ? {
            ...period,
            [`period_score_${team === "team1" ? "team1" : "team2"}`]: value ? parseInt(value, 10) : 0,
          }
          : period
      )
    );
  };
  const allPeriodsHidden = Object.values(periodScores).every(period => period.gamePeriod?.hidden);

  if (loading) return null;
  if (!game) return <p>Game not found.</p>;


  // Find the Final period's score
  const finalPeriod = periodScores.find(period => period.gamePeriod.name === "Final");

  // Extract the Final period score for team 1
  const finalScoreTeam1 = finalPeriod ? finalPeriod.period_score_team1 : game?.score_team1;
  const finalScoreTeam2 = finalPeriod ? finalPeriod.period_score_team2 : game?.score_team2;
  const getStatId = (abbreviation) => stats.find(s => s.abbreviation === abbreviation)?.id;



  const has2PM = !!getStatId('2PM');
  const has3PM = !!getStatId('3PM');
  const has2PA = !!getStatId('2PA');
  const has3PA = !!getStatId('3PA');

  const allowFGMInput = !(has2PM || has3PM);
  const allowFGAInput = !(has2PA || has3PA);

  console.log(game)


  return (
    <div className="game-container">
      {isSaving && (
        <div className="saving-overlay">
          <div className="saving-row">
            <div className="saving-text">Saving stats...</div>
            <div className="basketball">
              <div className="ball">
                <div className="lines"></div>
              </div>
              <div className="shadow"></div>
            </div>
          </div>
        </div>
      )}



      <div className="edit-actions">
        <button className="toggle-button" onClick={() => {
          if (editMode) {
            setStatValues(originalStatValues); // ✅ revert to saved copy
          }
          setEditMode(!editMode);
        }}
        >
          {editMode ? 'Cancel Edit Stats' : 'Edit Stats'}
        </button>
        <button className="toggle-button" onClick={() => {
          if (editFinalScores) {
            setFinalScoreForm(originalFinalScoreForm); // ✅ revert
          }
          setEditFinalScores(!editFinalScores);
        }}
        >
          {editFinalScores ? 'Cancel Final Scores' : 'Edit Final Scores'}
        </button>
        <button className="toggle-button" onClick={() => {
          setShowModal(true);
          setFormData({
            video_url: game?.video_url || '',
            location: game?.location || '',
            date: game?.date || '',
            time: game?.time || '',
          });
        }}>
          Edit Game
        </button>
      </div>
      {editFinalScores && (
        <div className="final-score-editor">
          <h4>Edit Final Score</h4>
          <label>
            {game.homeTeam?.name || "Home"}:
            <input
              type="number"
              value={finalScoreForm.score_home === '' ? '' : finalScoreForm.score_home}

              onChange={(e) => {
                const value = e.target.value;
                setFinalScoreForm((prev) => ({
                  ...prev,
                  score_home: value === '' ? '' : parseInt(value),
                }));
              }}
            />
          </label>
          <label>
            {game.awayTeam?.name || "Away"}:
            <input
              type="number"
              value={finalScoreForm.score_away === '' ? '' : finalScoreForm.score_away}

              onChange={(e) => {
                const value = e.target.value;
                setFinalScoreForm((prev) => ({
                  ...prev,
                  score_away: value === '' ? '' : parseInt(value),
                }));
              }}

            />
          </label>
          <button className="submit-button" onClick={handleSaveFinalScore}>
            Save Final Score
          </button>
        </div>
      )}

      {/* Home Team Stats */}
      <div className="team-container-home">
        <div className="game-video-container">
          {game?.video_url && (
            <div className="video-box">
              <iframe
                width="100%"
                height="315"
                src={game.video_url}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Game Video"
                onError={() => alert("There was an error loading the video. Please try again later.")}
              />
            </div>
          )}
        </div>

        <div className="game-details">
          <p>Location: {game.location}</p>
          <p>Game Time: {game?.time ? formatInTimeZone(`${game.date}T${game.time}`, 'America/Los_Angeles', 'h:mm a') : 'TBD'}</p>
          <p>Date: {game?.date ? formatInTimeZone(game.date, 'America/Los_Angeles', 'MMM d, yyyy') : 'N/A'}</p>
        </div>

        {/* {allPeriodsHidden &&
          <table className="gamePeriods-table">
            <thead>
              <tr>
                <th>Team Name</th>
                {periodScores.map((period) => (
                  <th key={period.id}>{period.gamePeriod?.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{game?.home_team?.team?.name || "Unknown Team"}</td>
                {periodScores.map((period) => (
                  <td key={period.id}>
                    {!editModeScores ? (
                      <span>{period.period_score_team1 || 0}</span>
                    ) : (
                      <input
                        type="number"
                        value={period.period_score_team1}
                        onChange={(e) => handleScoreChange(period.id, "team1", e.target.value)}
                      />
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                <td>{game?.away_team?.team?.name || "Unknown Team"}</td>
                {periodScores.map((period) => (
                  <td key={period.id}>
                    {!editModeScores ? (
                      <span>{period?.period_score_team2 || 0}</span>
                    ) : (
                      <input
                        type="number"
                        value={period?.period_score_team2}
                        onChange={(e) => handleScoreChange(period.id, "team2", e.target.value)}
                      />
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        }
        {editModeScores && (
          <button className="submit-button" onClick={handleSaveScores}>
            Save Scores
          </button>
        )} */}

        <h3 className="team-header">
          Home Team: {game?.home_team?.team?.name || "Unknown Team"}
          ({finalScoreForm.score_home})
        </h3>
        <table className="gameAuth-stat-table">
          <thead>
            <tr>
              <th className="player-column">Player</th>
              {stats.map((stat) => (
                <th key={stat.id} className="stat-column">{stat.abbreviation}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {game?.homeTeam?.players.map((player) => (
              <tr
                key={player.id}
                className={activePlayerId === player.id ? "active-row" : ""}
              >
                <td className="player-name">{player.firstName} {player.lastName}</td>
                {stats.map((stat) => (
                  <td key={stat.id}>
                    {!editMode ||
                      (stat.abbreviation === 'FGM' && !allowFGMInput) ||
                      (stat.abbreviation === 'FGA' && !allowFGAInput) ||
                      ['FG%', 'FT%', '2P%', '3P%'].includes(stat.abbreviation) ? (
                      <span className="stat-value">
                        {(() => {
                          //FGM
                          if (stat.abbreviation === 'FGM') {
                            const twoPMId = getStatId('2PM');
                            const threePMId = getStatId('3PM');
                            const has2P = !!twoPMId;
                            const has3P = !!threePMId;

                            if (has2P || has3P) {
                              const twoPM = has2P ? (statValues[`${player.id}-${twoPMId}`] || 0) : 0;
                              const threePM = has3P ? (statValues[`${player.id}-${threePMId}`] || 0) : 0;
                              return twoPM + threePM;
                            } else {
                              return statValues[`${player.id}-${stat.id}`] || 0;
                            }
                          }


                          // FGA
                          if (stat.abbreviation === 'FGA') {
                            const twoPAId = getStatId('2PA');
                            const threePAId = getStatId('3PA');
                            const has2PA = !!twoPAId;
                            const has3PA = !!threePAId;

                            if (has2PA || has3PA) {
                              const twoPA = has2PA ? (statValues[`${player.id}-${twoPAId}`] || 0) : 0;
                              const threePA = has3PA ? (statValues[`${player.id}-${threePAId}`] || 0) : 0;
                              return twoPA + threePA;
                            } else {
                              return statValues[`${player.id}-${stat.id}`] || 0;
                            }
                          }

                          // FG%
                          if (stat.abbreviation === 'FG%') {
                            const twoPM = statValues[`${player.id}-${getStatId('2PM')}`] || 0;
                            const threePM = statValues[`${player.id}-${getStatId('3PM')}`] || 0;
                            const twoPA = statValues[`${player.id}-${getStatId('2PA')}`] || 0;
                            const threePA = statValues[`${player.id}-${getStatId('3PA')}`] || 0;

                            const fgm = twoPM + threePM;
                            const fga = twoPA + threePA;

                            return fga ? ((fgm / fga) * 100).toFixed(1) + '%' : '0.0%';
                          }

                          // FT%
                          if (stat.abbreviation === 'FT%') {
                            const ftm = statValues[`${player.id}-${getStatId('FTM')}`] || 0;
                            const fta = statValues[`${player.id}-${getStatId('FTA')}`] || 0;
                            return fta ? ((ftm / fta) * 100).toFixed(1) + '%' : 0;
                          }

                          // 2P%
                          if (stat.abbreviation === '2P%') {
                            const made = statValues[`${player.id}-${getStatId('2PM')}`] || 0;
                            const attempted = statValues[`${player.id}-${getStatId('2PA')}`] || 0;
                            return attempted ? ((made / attempted) * 100).toFixed(1) + '%' : 0;
                          }

                          // 3P%
                          if (stat.abbreviation === '3P%') {
                            const made = statValues[`${player.id}-${getStatId('3PM')}`] || 0;
                            const attempted = statValues[`${player.id}-${getStatId('3PA')}`] || 0;
                            return attempted ? ((made / attempted) * 100).toFixed(1) + '%' : 0;
                          }

                          // Default stat
                          return statValues[`${player.id}-${stat.id}`] || 0;
                        })()}
                      </span>

                    ) : (
                      <input
                        type="number"
                        className="stat-input"
                        value={statValues[`${player.id}-${stat.id}`] ?? ""}
                        onFocus={() => setActivePlayerId(player.id)}
                        onChange={(e) =>
                          handleInputChange(player.id, stat.id, e.target.value === "" ? "" : parseInt(e.target.value, 10))
                        }
                      />


                    )}
                  </td>
                ))}
              </tr>
            ))}
            {/* ✅ Total Row for Home Team */}
            <tr className="total-row">
              <td className="player-name"><strong>Total</strong></td>
              {stats.map((stat) => {
                let validPercents = 0;
                let total = game?.homeTeam?.players.reduce((sum, player) => {
                  if (['FG%', '2P%', '3P%'].includes(stat.abbreviation)) {
                    if (stat.abbreviation === 'FG%') {
                      return sum; // Skip inside reduce
                    }

                    const get = (abbr) => statValues[`${player.id}-${getStatId(abbr)}`] || 0;
                    let percent = null;

                    if (stat.abbreviation === '2P%') {
                      percent = get('2PA') ? (get('2PM') / get('2PA')) * 100 : null;
                    } else if (stat.abbreviation === '3P%') {
                      percent = get('3PA') ? (get('3PM') / get('3PA')) * 100 : null;
                    }

                    if (percent !== null) {
                      validPercents++;
                      return sum + percent;
                    }
                    return sum;
                  }

                  // All other stats
                  const val = stat.abbreviation === 'FGM'
                    ? (statValues[`${player.id}-${getStatId('2PM')}`] || 0) +
                    (statValues[`${player.id}-${getStatId('3PM')}`] || 0)
                    : stat.abbreviation === 'FGA'
                      ? (statValues[`${player.id}-${getStatId('2PA')}`] || 0) +
                      (statValues[`${player.id}-${getStatId('3PA')}`] || 0)
                      : statValues[`${player.id}-${stat.id}`] || 0;

                  return sum + val;
                }, 0);

                // 🧠 Render logic AFTER reduce
                if (stat.abbreviation === 'FG%') {
                  const totalFGM = game.homeTeam?.players.reduce((sum, player) => {
                    const twoPM = statValues[`${player.id}-${getStatId('2PM')}`] || 0;
                    const threePM = statValues[`${player.id}-${getStatId('3PM')}`] || 0;
                    return sum + twoPM + threePM;
                  }, 0);

                  const totalFGA = game.homeTeam?.players.reduce((sum, player) => {
                    const twoPA = statValues[`${player.id}-${getStatId('2PA')}`] || 0;
                    const threePA = statValues[`${player.id}-${getStatId('3PA')}`] || 0;
                    return sum + twoPA + threePA;
                  }, 0);

                  const fgPercent = totalFGA ? (totalFGM / totalFGA) * 100 : 0;

                  return (
                    <td key={stat.id}>
                      <strong>{fgPercent.toFixed(1)}%</strong>
                    </td>
                  );
                }

                return (
                  <td key={stat.id}>
                    <strong>
                      {['2P%', '3P%'].includes(stat.abbreviation)
                        ? validPercents > 0
                          ? (total / validPercents).toFixed(1) + '%'
                          : ''
                        : total}
                    </strong>
                  </td>
                );
              })}
            </tr>


          </tbody>
        </table>
      </div>

      {/* Away Team Stats */}
      <div className="team-container-away">
        <h3 className="team-header">
          Away Team: {game?.away_team?.team?.name || "Unknown Team"} ({finalScoreForm.score_away})
        </h3>
        <table className="gameAuth-stat-table">
          <thead>
            <tr>
              <th className="player-column">Player</th>
              {stats.map((stat) => (
                <th key={stat.id} className="stat-column">{stat.abbreviation}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {game?.awayTeam?.players.map((player) => (
              <tr
                key={player.id}
                className={activePlayerId === player.id ? "active-row" : ""}
              >
                <td className="player-name">{player.firstName} {player.lastName} </td>
                {stats.map((stat) => (
                  <td key={stat.id}>
                    {!editMode ||
                      (stat.abbreviation === 'FGM' && !allowFGMInput) ||
                      (stat.abbreviation === 'FGA' && !allowFGAInput) ||
                      ['FG%', 'FT%', '2P%', '3P%'].includes(stat.abbreviation) ? (
                      <span className="stat-value">
                        {(() => {
                          //FGM
                          if (stat.abbreviation === 'FGM') {
                            const twoPMId = getStatId('2PM');
                            const threePMId = getStatId('3PM');
                            const has2P = !!twoPMId;
                            const has3P = !!threePMId;

                            if (has2P || has3P) {
                              const twoPM = has2P ? (statValues[`${player.id}-${twoPMId}`] || 0) : 0;
                              const threePM = has3P ? (statValues[`${player.id}-${threePMId}`] || 0) : 0;
                              return twoPM + threePM;
                            } else {
                              return statValues[`${player.id}-${stat.id}`] || 0;
                            }
                          }


                          // FGA
                          if (stat.abbreviation === 'FGA') {
                            const twoPAId = getStatId('2PA');
                            const threePAId = getStatId('3PA');
                            const has2PA = !!twoPAId;
                            const has3PA = !!threePAId;

                            if (has2PA || has3PA) {
                              const twoPA = has2PA ? (statValues[`${player.id}-${twoPAId}`] || 0) : 0;
                              const threePA = has3PA ? (statValues[`${player.id}-${threePAId}`] || 0) : 0;
                              return twoPA + threePA;
                            } else {
                              return statValues[`${player.id}-${stat.id}`] || 0;
                            }
                          }

                          // FG%
                          if (stat.abbreviation === 'FG%') {
                            const twoPM = statValues[`${player.id}-${getStatId('2PM')}`] || 0;
                            const threePM = statValues[`${player.id}-${getStatId('3PM')}`] || 0;
                            const twoPA = statValues[`${player.id}-${getStatId('2PA')}`] || 0;
                            const threePA = statValues[`${player.id}-${getStatId('3PA')}`] || 0;

                            const fgm = twoPM + threePM;
                            const fga = twoPA + threePA;

                            return fga ? ((fgm / fga) * 100).toFixed(1) + '%' : '0.0%';
                          }

                          // FT%
                          if (stat.abbreviation === 'FT%') {
                            const ftm = statValues[`${player.id}-${getStatId('FTM')}`] || 0;
                            const fta = statValues[`${player.id}-${getStatId('FTA')}`] || 0;
                            return fta ? ((ftm / fta) * 100).toFixed(1) + '%' : 0;
                          }

                          // 2P%
                          if (stat.abbreviation === '2P%') {
                            const made = statValues[`${player.id}-${getStatId('2PM')}`] || 0;
                            const attempted = statValues[`${player.id}-${getStatId('2PA')}`] || 0;
                            return attempted ? ((made / attempted) * 100).toFixed(1) + '%' : 0;
                          }

                          // 3P%
                          if (stat.abbreviation === '3P%') {
                            const made = statValues[`${player.id}-${getStatId('3PM')}`] || 0;
                            const attempted = statValues[`${player.id}-${getStatId('3PA')}`] || 0;
                            return attempted ? ((made / attempted) * 100).toFixed(1) + '%' : 0;
                          }

                          // Default stat
                          return statValues[`${player.id}-${stat.id}`] || 0;
                        })()}
                      </span>
                    ) : (
                      <input
                        type="number"
                        className="stat-input"
                        value={statValues[`${player.id}-${stat.id}`] ?? ""}
                        onFocus={() => setActivePlayerId(player.id)} // 👈 add this
                        onChange={(e) =>
                          handleInputChange(player.id, stat.id, parseInt(e.target.value) || 0)
                        }
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {/* ✅ Total Row for Away Team */}
            <tr className="total-row">
              <td className="player-name"><strong>Total</strong></td>
              {stats.map((stat) => {
                let validPercents = 0;
                let total = game?.awayTeam?.players.reduce((sum, player) => {
                  if (['FG%', '2P%', '3P%'].includes(stat.abbreviation)) {
                    if (stat.abbreviation === 'FG%') {
                      return sum; // Skip inside reduce
                    }

                    const get = (abbr) => statValues[`${player.id}-${getStatId(abbr)}`] || 0;
                    let percent = null;

                    if (stat.abbreviation === '2P%') {
                      percent = get('2PA') ? (get('2PM') / get('2PA')) * 100 : null;
                    } else if (stat.abbreviation === '3P%') {
                      percent = get('3PA') ? (get('3PM') / get('3PA')) * 100 : null;
                    }

                    if (percent !== null) {
                      validPercents++;
                      return sum + percent;
                    }
                    return sum;
                  }

                  // All other stats
                  const val = stat.abbreviation === 'FGM'
                    ? (statValues[`${player.id}-${getStatId('2PM')}`] || 0) +
                    (statValues[`${player.id}-${getStatId('3PM')}`] || 0)
                    : stat.abbreviation === 'FGA'
                      ? (statValues[`${player.id}-${getStatId('2PA')}`] || 0) +
                      (statValues[`${player.id}-${getStatId('3PA')}`] || 0)
                      : statValues[`${player.id}-${stat.id}`] || 0;

                  return sum + val;
                }, 0);

                // 🧠 Render logic AFTER reduce
                if (stat.abbreviation === 'FG%') {
                  const totalFGM = game.awayTeam?.players.reduce((sum, player) => {
                    const twoPM = statValues[`${player.id}-${getStatId('2PM')}`] || 0;
                    const threePM = statValues[`${player.id}-${getStatId('3PM')}`] || 0;
                    return sum + twoPM + threePM;
                  }, 0);

                  const totalFGA = game.awayTeam?.players.reduce((sum, player) => {
                    const twoPA = statValues[`${player.id}-${getStatId('2PA')}`] || 0;
                    const threePA = statValues[`${player.id}-${getStatId('3PA')}`] || 0;
                    return sum + twoPA + threePA;
                  }, 0);

                  const fgPercent = totalFGA ? (totalFGM / totalFGA) * 100 : 0;

                  return (
                    <td key={stat.id}>
                      <strong>{fgPercent.toFixed(1)}%</strong>
                    </td>
                  );
                }

                return (
                  <td key={stat.id}>
                    <strong>
                      {['2P%', '3P%'].includes(stat.abbreviation)
                        ? validPercents > 0
                          ? (total / validPercents).toFixed(1) + '%'
                          : ''
                        : total}
                    </strong>
                  </td>
                );
              })}
            </tr>


          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Edit Game Details</h2>
            <form onSubmit={handleUpdateGameDetails}>
              <label>
                Video URL:
                <input
                  type="text"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                />
              </label>
              <label>
                Location:
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </label>
              <label>
                Time:
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                />
              </label>
              <label>
                Date
                <input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />

              </label>
              <button type="submit">Save Changes</button>
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}


      {editMode && (
        <button className="submit-button" onClick={handleSubmit}>
          Save Stats
        </button>
      )}
    </div>
  );
}

export default GameAuth;

